import { SourceStrip } from '@ui/source/SourceStrip';
import Tag from '@app/component/tag/Tag';
import PriceEditModal from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/price-edit/PriceEditModal';
import PricingRoomWithPriceOutput from '@app/model/pricing-automation/output/PricingRoomWithPriceOutput';
import { recommendationTableRooms } from './PriceRecommendationDrawerContents.utilities';
import { usePricingRights } from '@util/PricingRightsHook';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import _isEmpty from 'lodash/isEmpty';
import Message from '@app/component/message/Message';
import {
	ArrowDownIcon,
	ArrowUpIcon,
	SparklesIcon,
} from '@heroicons/react/20/solid';
import RateChange from '@ui/stat/RateChange';
import PriceRecommendationStateDriver from '@app/model/market_state/PriceRecommendationStateDriver';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import FormButton from '@app/component/form/button/FormButton';
import useModal from '@app/component/overlays/modal/ModalRef';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Tabs from '@app/component/tabs/Tabs';
import HotelRoomMappingType, {
	ROOM_MAPPING_TYPE_ORDER,
	sortByRoomMappingType,
} from '@app/model/market_state/HotelRoomMappingType';
import PriceRecommendationStateDriverType from '@app/model/market_state/PriceRecommendationStateDriverType';
import useAppForm from '@app/site/_form';
import { RadioField } from '@app/site/_form/field/RadioField';
import { TextAreaField } from '@app/site/_form/field/TextAreaField';
import { SubmitButton } from '@app/site/_form/button/SubmitButton';
import { NumberField } from '@app/site/_form/field/NumberField';
import { FieldErrors } from '@app/site/_form/field/FieldErrors';
import PriceRecommendationFeedbackInput from '@app/model/pricing-automation/input/PriceRecommendationFeedbackInput';
import PriceRecommendationFeedbackVerdict from '@app/model/pricing-automation/PriceRecommendationFeedbackVerdict';
import { priceRecommendationFeedbackSchema } from '@app/model/pricing-automation/input/PriceRecommendationFeedback.validation';
import PricingArchitectureDerivativeRuleType from '@app/model/pricing-automation/PricingArchitectureDerivativeRuleType';
import Utilities from '@util/Utilities';
import PriceRecommendationDrawerBookingPace
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/overlay/PriceRecommendationDrawerBookingPace';
import MarketStateService from '@app/model/market_state/service/MarketStateService';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import PriceRecommendationRoomState from '@app/model/market_state/PriceRecommendationRoomState';
import PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import SubmitPricingResultOutput from '@app/model/pricing-automation/output/SubmitPricingResultOutput';
import SubmitMonthlyPricingResultOutputState from '@app/model/pricing-automation/output/SubmitMonthlyPricingResultOutputState';
import PriceSubmissionBlockedItems from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/PriceSubmissionBlockedItems';
import AvailabilityDailyStateProvider, {
	AvailabilityDailyStateContext,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/AvailabillityDailyStateProvider';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import InfoTooltip from '@ui/tooltip/InfoTooltip';
import {
	RecommendationLimitLines,
	RecommendationLimitTag,
	useRecommendationLimit,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/RecommendationLimitExplanation';
import {
	DRIVER_IMPACT_LEVEL_LABELS,
	findLowestAnchorRoom,
	getDriverImpactLevel,
	roomPriceChangePercent,
	orderRecommendationDrivers,
	selectRecommendationAnalysisSource,
} from './PriceRecommendationDrawerContents.utilities';
import { DayKind, findDayKind, findLowerRoomType, recommendedChangeRate } from './DayPricing.utilities';

const PriceRecommendationDrawerContents: React.FunctionComponent<{ statDate: string, onSubmitPrices: () => any }>
	= ({ statDate, onSubmitPrices }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const results = useQueries({
		queries: [
			{
				queryKey: PricingAutomationQueryKeys.pricingArchitectures(companyHotel.id),
				queryFn: () => PricingAutomationService.findCompanyHotelPricingArchitectures(companyHotel.id),
				enabled: companyHotel?.id !== undefined,
			}, {
				queryKey: MarketStateGraphQLQueryKeys.priceRecommendationStateForStatDate(companyHotel.id, statDate),
				queryFn: () => MarketStateService.findLatestPriceRecommendationStateForStatDate(companyHotel.id, statDate),
				enabled: companyHotel?.id !== undefined,
			},
		],
	});

	if (results.some(s => s.isLoading)) {
		return <TablePlaceholder />;
	}

	const architectures = results[0].data;
	const recommendationStates = results[1].data;

	if (results.some(s => s.isError) || _isEmpty(architectures)) {
		return <Message state="error">Error loading pricing data</Message>;
	}

	// No engine result for the date: the drawer still shows the pickup and the live rates, and Edit Pricing.
	if (_isEmpty(recommendationStates)) {
		return <AvailabilityDailyStateProvider companyHotel={companyHotel} statDate={statDate}>
			<NoEngineResultContents statDate={statDate} architectures={architectures!} onSubmitPrices={onSubmitPrices} />
		</AvailabilityDailyStateProvider>;
	}

	return <AvailabilityDailyStateProvider companyHotel={companyHotel} statDate={statDate}>
		<Contents recommendationStates={recommendationStates!}
			onSubmitPrices={onSubmitPrices}
			architectures={architectures!}
			statDate={statDate} />
	</AvailabilityDailyStateProvider>;

};


const Contents: React.FunctionComponent<{
	recommendationStates: PriceRecommendationState[]
	architectures: PricingArchitecture[],
	onSubmitPrices: () => any,
	statDate: string
}>
	= ({ recommendationStates, architectures, onSubmitPrices, statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();

	sortByRoomMappingType(recommendationStates, 'roomMappingType');

	const hasMultipleRecommendations = recommendationStates.length > 1;

	const tabs = [];
	if (hasMultipleRecommendations)
		tabs.push({
			label: 'All',
			value: 0,
		});

	recommendationStates.map((reco, idx) => (
		tabs.push({
			label: Utilities.snakeCaseToWords(reco.roomMappingType),
			value: idx + (hasMultipleRecommendations ? 1 : 0),
		})
	));

	tabs.push({
		label: 'Feedback',
		outlined: true,
		value: tabs.length,
	});

	const [selectedTab, setSelectedTab] = React.useState<number>(0);


	const isTabAll = tabs[selectedTab].label === 'All';
	const isTabFeedback = tabs[selectedTab].label === 'Feedback';

	return <div className="mb-6">
		<Tabs
			options={tabs}
			value={selectedTab}
			onChange={(value) => setSelectedTab(typeof value === 'string' ? parseInt(value) : value)}
		/>

		{!isTabFeedback && !isTabAll && <ArchitectureTabContent onSubmitPrices={onSubmitPrices}
			dayStates={recommendationStates}
			recommendationState={recommendationStates[selectedTab - (hasMultipleRecommendations ? 1 : 0)]}
			architecture={architectures.find(arch => arch.id === recommendationStates[selectedTab - (hasMultipleRecommendations ? 1 : 0)].architectureId)!}
			statDate={statDate} />}

		{isTabAll && <AllArchitecturesContent companyHotel={companyHotel}
			onSubmitPrices={onSubmitPrices}
			recommendationStates={recommendationStates}
			architectures={architectures}
			statDate={statDate} />}

		{isTabFeedback && <FeedbackContent companyHotel={companyHotel} recommendationStates={recommendationStates} />}
	</div>;
};

const FeedbackContent: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	recommendationStates: PriceRecommendationState[]
}> = ({ companyHotel, recommendationStates }) => {

	const [message, setMessage] = useState<{ state: 'success' | 'error', text: string } | undefined>(undefined);

	useEffect(() => {
		if (!message) {
			return;
		}

		const timeout = setTimeout(() => setMessage(undefined), 4000);

		return () => clearTimeout(timeout);
	}, [message]);

	const createFeedbackMutation = useMutation({
		mutationFn: PricingAutomationService.createPricingRecommendationFeedback,
		onSuccess: () => {
			setMessage({ state: 'success', text: 'Thank you. Your feedback reached the pricing team.' });
			form.reset();
		},
		onError: () => {
			setMessage({ state: 'error', text: 'We could not send your feedback. Please try again.' });
		},
	});

	const recommendationOptions = recommendationStates.map(r => ({
		label: Utilities.snakeCaseToWords(r.roomMappingType),
		value: r.id,
	}));

	const verdictOptions = [
		{ label: 'The recommended rate is too high', value: PriceRecommendationFeedbackVerdict.RATE_TOO_HIGH },
		{ label: 'The recommended rate is too low', value: PriceRecommendationFeedbackVerdict.RATE_TOO_LOW },
		{ label: 'Keep the current rate', value: PriceRecommendationFeedbackVerdict.KEEP_CURRENT_RATE },
	];

	const form = useAppForm({
		defaultValues: {
			companyHotelId: companyHotel.id,
			stateId: recommendationStates[0].id,
			verdict: undefined as PriceRecommendationFeedbackVerdict | undefined,
			comment: '',
			suggestedPrice: undefined as number | undefined,
		} as PriceRecommendationFeedbackInput,
		validators: {
			onChange: priceRecommendationFeedbackSchema,
		},
		onSubmit: async ({ value }) => {
			await createFeedbackMutation.mutateAsync({
				input: {
					companyHotelId: value.companyHotelId,
					stateId: value.stateId,
					verdict: value.verdict,
					comment: value.comment.trim(),
					suggestedPrice: value.suggestedPrice,
				},
			}).catch(() => undefined);
		},
	});

	const currency = companyHotel.settings.main.currency;

	return <form.AppForm>
		<form onSubmit={(e) => {
			e.preventDefault();
			e.stopPropagation();
			void form.handleSubmit();
		}}>
			{message && <Message state={message.state} extraClassName="mt-6">{message.text}</Message>}
			<div className="divide-y divide-gray-200 overflow-hidden rounded-md ring-1 ring-black ring-opacity-10 mt-8">
				<div className="px-3 py-3 bg-gray-50">
					<h6 className="text-sm font-semibold text-gray-900">Share Feedback</h6>
				</div>
				<div className="px-3 py-3 text-sm">
					<p className="text-xs font-medium text-gray-700 mb-3">
						Help us improve the recommendation. Pick a room, choose your verdict, and tell us what you would do instead.
					</p>
					<div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
						<form.AppField name="stateId">
							{() => recommendationOptions.map(o => <RadioField key={o.value} {...o} variant="button" />)}
						</form.AppField>
					</div>
					<form.Subscribe selector={state => state.values.stateId}>
						{stateId => {
							const state = recommendationStates.find(r => r.id === stateId);
							const anchor = state?.minimumBasePriceRoomStates?.some(room => room.isAnchor) === true
								? state.findLowestPriceForAnchorLogRoom() : undefined;
							if (!state || !anchor) return null;
							const rate = state.effectiveChangeRate;
							return <p className="mt-3 text-xs text-gray-500">
								{anchor.roomName}: current rate {Utilities.formatAmount(anchor.basePrice, currency, 0)},
								{' '}recommended rate <span className="font-semibold text-gray-900">{Utilities.formatAmount(anchor.finalPrice, currency, 0)}</span>
								{rate !== 0 && <span className={rate > 0 ? 'text-green-600' : 'text-red-600'}> ({rate > 0 ? '+' : ''}{Utilities.formatDouble(rate * 100, 1)}%)</span>}
								{rate === 0 && <span> (no change)</span>}
							</p>;
						}}
					</form.Subscribe>
					<div className="space-y-2 mt-4">
						<form.AppField name="verdict">
							{() => verdictOptions.map(o => {
								return <div key={o.value} className="flex items-center"><RadioField {...o} /></div>;
							})}
						</form.AppField>
					</div>
					<form.AppField name="comment">
						{field => <div className="mt-4">
							<div className="flex flex-wrap">
								<div className="label">Your comment</div>
								<span className="text-xs leading-6 text-gray-500 ml-2 italic">What would you do instead, and why?</span>
							</div>
							<div
								className="mt-0.5 overflow-hidden rounded-lg outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-blue-600">
								<TextAreaField
									rows={4}
									placeholder="Example: 3 days out and 15 rooms still open. A raise will not sell them, so I would keep the rate."
								/>
							</div>
							<FieldErrors meta={field.state.meta} />
						</div>}
					</form.AppField>
					<div className="mt-4 sm:w-80">
						<form.AppField name="suggestedPrice">
							{() => <NumberField
								label="Your rate"
								labelHint={`the rate you would set, optional, in ${Utilities.formatCurrency(currency)}`}
								min={0}
								step={1}
								placeholder="e.g. 175"
							/>}
						</form.AppField>
					</div>

					<div className="mt-4 flex justify-end">
						<SubmitButton state="primary" submitOnClick={false}>Send feedback</SubmitButton>
					</div>
				</div>
			</div>
		</form>
	</form.AppForm>;
};

const AllArchitecturesContent: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	recommendationStates: PriceRecommendationState[],
	architectures: PricingArchitecture[],
	onSubmitPrices: () => void
	statDate: string
}> = ({ companyHotel, recommendationStates, architectures, onSubmitPrices, statDate }) => {
	const analysisSource = selectRecommendationAnalysisSource<
		HotelRoomMappingType,
		PriceRecommendationRoomState,
		PriceRecommendationState
	>(
		recommendationStates,
		ROOM_MAPPING_TYPE_ORDER
	);

	// The Limit Explanation of the room type that feeds the Recommendation Analysis card.
	const limitView = useRecommendationLimit(analysisSource?.state);

	const hasTableRooms = recommendationStates.some(state => recommendationTableRooms(state.minimumBasePriceRoomStates).length > 0);
	// A Held Recommendation: the engine ran, but no room type has a valid change.
	const isHeld = !recommendationStates.some(state => state.isValid);

	const dayKind = findDayKind(recommendationStates);

	return <div className="mt-6 space-y-4">
		<RenderDayHeader recommendationStates={recommendationStates} ruleApplies={limitView?.limited === true} />
		{dayKind === 'valid' && analysisSource !== undefined && limitView !== undefined &&
			<div className="overflow-hidden rounded-md bg-blue-50 ring-1 ring-blue-500 ring-opacity-30 shadow-sm">
				<div className="px-4 py-3">
					{/* A text title, not the tag pill: every line of the box starts at the same left edge. */}
					<p className="text-sm font-semibold text-amber-700">
						{limitView.text.tag}
						<span className="font-normal text-gray-500"> · Based on {Utilities.snakeCaseToWords(analysisSource.state.roomMappingType)}</span>
					</p>
					<div className="mt-1">
						<RecommendationLimitLines view={limitView} />
					</div>
				</div>
			</div>}
		{dayKind === 'valid' && analysisSource !== undefined && <RenderRecommendationAnalysis recommendationState={analysisSource.state} />}
		<PriceRecommendationDrawerBookingPace recommendation={recommendationStates[0]} showTotalsOnly />
		{hasTableRooms && <PriceTableWrapper hasProposal={!isHeld}>
			{recommendationStates.map((recommendationState, idx) => {
				const architecture = architectures.find(arch => arch.id === recommendationState.architectureId)!;
				return <RenderPriceTable key={idx} companyHotel={companyHotel} recommendationState={recommendationState}
					architecture={architecture} showRecommendation={recommendationState.isValid} />;
			})}
		</PriceTableWrapper>}
		<div className="flex gap-x-2 justify-end -mt-3">
			<RenderEditPricingButton companyHotel={companyHotel}
				onSubmit={onSubmitPrices}
				recommendationStates={recommendationStates}
				architectures={architectures}
				statDate={statDate} />
		</div>
	</div>;
};

const ArchitectureTabContent: React.FunctionComponent<{
	recommendationState: PriceRecommendationState,
	// Every room type of the day: the room-order text names the type below this one.
	dayStates: PriceRecommendationState[],
	architecture: PricingArchitecture,
	onSubmitPrices: () => any,
	statDate: string
}> = ({ recommendationState, dayStates, architecture, onSubmitPrices, statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	// The Limit Explanation of a Limited Recommendation: the tag under the rate, the two lines under the card.
	const limitView = useRecommendationLimit(recommendationState);

	if (recommendationState === undefined) return <Message state="error" extraClassName="mt-4">There is no recommendation found for
		this
		architecture</Message>;

	const analysisSource = selectRecommendationAnalysisSource<
		HotelRoomMappingType,
		PriceRecommendationRoomState,
		PriceRecommendationState
	>(
		[recommendationState],
		ROOM_MAPPING_TYPE_ORDER
	);
	const lowestPriceLogRoom = analysisSource?.room;
	const roomChangePercent = lowestPriceLogRoom
		? roomPriceChangePercent(lowestPriceLogRoom.basePrice, lowestPriceLogRoom.finalPrice)
		: 0;
	const hasValidRecommendation = recommendationState.isValid;

	return <div className="mt-4 space-y-4">
		<RenderDayHeader recommendationStates={[recommendationState]} dayStates={dayStates} ruleApplies={limitView?.limited === true} />
		{lowestPriceLogRoom &&
			<div className="overflow-hidden rounded-md bg-blue-50 ring-1 ring-blue-500 ring-opacity-30 shadow-sm">
				<div className={`px-4 pt-5 ${limitView !== undefined ? 'pb-3' : 'pb-4'}`}>
					<p className="mb-3 text-xs font-medium text-blue-700 break-words">WebHotelier · {lowestPriceLogRoom.roomName}</p>
					<div className="sm:grid grid-cols-1 gap-4 sm:grid-cols-2 space-y-2 sm:space-y-0">
						<div className="border-r border-blue-200 pr-4">
							<p className="text-sm font-medium text-gray-500">Base rate</p>
							<div className="flex items-baseline">
								<p
									className="text-2xl font-semibold text-gray-900">{Utilities.formatAmount(lowestPriceLogRoom.basePrice, companyHotel.settings.main.currency, 0)}</p>
							</div>
						</div>
						<div><p className="truncate text-sm font-medium text-blue-600">Proposed rate</p>
							<div className="flex items-baseline gap-x-2">
								<p
									className="text-2xl font-semibold text-gray-900">{Utilities.formatAmount(lowestPriceLogRoom.finalPrice, companyHotel.settings.main.currency, 0)}</p>
								<div className="flex items-baseline text-sm font-semibold text-green-600">
									{roomChangePercent !== 0 && <p
										className={`flex items-center text-sm font-semibold ${roomChangePercent > 0 ? 'text-green-600' : 'text-rose-600'}`}>
										{roomChangePercent > 0 ? <ArrowUpIcon className="w-4 h-4" /> :
											<ArrowDownIcon className="w-4 h-4" />}
										<span>{Math.abs(roomChangePercent)}%</span>
									</p>}
								</div>
							</div>
							{limitView?.limited === true && <div className="mt-1.5 flex items-center">
								<RecommendationLimitTag view={limitView} />
							</div>}
						</div>
					</div>
				</div>
				{limitView !== undefined && <div className="px-4 pt-2.5 pb-3 border-t border-blue-200">
					<RecommendationLimitLines view={limitView} />
				</div>}
			</div>}
		{analysisSource && findDayKind([recommendationState]) === 'valid' && <RenderRecommendationAnalysis recommendationState={analysisSource.state} />}
		{recommendationState.marketStatePromptId !== undefined && recommendationState.marketStatePromptId !== null && recommendationState.marketStatePromptId !== 0 &&
			<PriceRecommendationDrawerBookingPace recommendation={recommendationState} />}
		{recommendationTableRooms(recommendationState.minimumBasePriceRoomStates).length > 0 && <PriceTableWrapper hasProposal={hasValidRecommendation}>
			<RenderPriceTable companyHotel={companyHotel} recommendationState={recommendationState}
				architecture={architecture} showRecommendation={hasValidRecommendation} />
		</PriceTableWrapper>}
		<div className="flex gap-x-2 justify-end -mt-3">
			<RenderEditPricingButton companyHotel={companyHotel}
				recommendationStates={[recommendationState]}
				architectures={[architecture]}
				architectureIds={[architecture.id]}
				onSubmit={onSubmitPrices}
				statDate={statDate} />
		</div>
	</div>;
};

const DRIVER_ORDER = [
	PriceRecommendationStateDriverType.COMPETITORS,
	PriceRecommendationStateDriverType.MARKET,
	PriceRecommendationStateDriverType.OCCUPANCY,
];

const DRIVER_DETAILS: Record<PriceRecommendationStateDriverType, { label: string, description: string }> = {
	[PriceRecommendationStateDriverType.COMPETITORS]: {
		label: 'Competitors',
		description: 'The effect of the competitor rates on this date. RoomPulse compares the live rates of your comp set against your rate.',
	},
	[PriceRecommendationStateDriverType.MARKET]: {
		label: 'Market',
		description: 'The effect of the market demand on this date. RoomPulse uses the demand score of your market and the rate percentiles.',
	},
	[PriceRecommendationStateDriverType.OCCUPANCY]: {
		label: 'Occupancy',
		description: 'The effect of your own hotel on this date. RoomPulse uses your occupancy and your Booking Pace against the normal pace.',
	},
};

const DRIVER_SEGMENT_COLORS = ['bg-blue-300', 'bg-blue-400', 'bg-blue-500', 'bg-blue-600'];

const RenderRecommendationAnalysis: React.FunctionComponent<{
	recommendationState: PriceRecommendationState
}> = ({ recommendationState }) => {
	const drivers = orderRecommendationDrivers(recommendationState.drivers, DRIVER_ORDER);

	return <>
		<div
			className="divide-y divide-gray-200 overflow-hidden rounded-md bg-gray-50 ring-1 ring-black ring-opacity-10">
			<div className="px-4 py-3">
				<h6 className="text-sm font-semibold text-gray-900">Recommendation Analysis</h6>
				<p className="mt-0.5 text-xs text-gray-500">
					Based on {Utilities.snakeCaseToWords(recommendationState.roomMappingType)} — the cheapest room
					type with a recommendation.
				</p>
			</div>
			<div className="space-y-2 divide-y divide-gray-100 text-sm">
				<div className="overflow-x-auto">
					<div className="inline-block min-w-full align-middle">
						<table className="min-w-full divide-y divide-gray-200 text-xs">
							<thead>
							<tr>
								<th scope="col" className="py-2 pl-4 pr-3 text-left font-bold text-gray-900 w-36">Factor</th>
								<th scope="col" className="py-2 px-3 text-left font-bold text-gray-900">Impact on Rate</th>
							</tr>
							</thead>
							<tbody className="divide-y divide-gray-200 bg-white">
							{drivers.map(driver => <RenderDriverRow key={driver.type} driver={driver} />)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	</>;
};

const PriceTableWrapper: React.FunctionComponent<PropsWithChildren<{ hasProposal?: boolean }>> = ({ hasProposal = true, children }) => {
	return <div
		className="divide-y divide-gray-200 overflow-hidden rounded-md bg-gray-50 ring-1 ring-black ring-opacity-10">
		<SourceStrip badge="WH" title="WebHotelier base rates"
			description={hasProposal ? 'Review the proposed room rates before you submit.' : 'The current rates of your rooms in WebHotelier.'} />
		<div className="space-y-2 divide-y divide-gray-100 text-sm">
			<div className="overflow-x-auto">
				<div className="inline-block min-w-full align-middle">
					<table className="w-full table-fixed divide-y divide-gray-200 text-xs">
						<thead>
						<tr>
							<th scope="col" className="py-2 px-1.5 text-left">Rooms</th>
							<th scope="col" className="w-8 py-2 px-1.5 text-left">Avl.</th>
							<th scope="col" className="w-12 py-2 px-1.5 text-left">Base</th>
							<th scope="col" className="w-16 py-2 px-1.5 text-left">Proposed</th>
							<th scope="col" className="w-14 py-2 px-1.5 text-left">Change</th>
						</tr>
						</thead>
						{children}
					</table>
				</div>
			</div>
		</div>
	</div>;
};

const RenderPriceTable: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	architecture: PricingArchitecture
	recommendationState: PriceRecommendationState
	showRecommendation: boolean
}> = ({ companyHotel, architecture, recommendationState, showRecommendation }) => {

	const { availabilityDailyState } = React.useContext(AvailabilityDailyStateContext);
	const rooms = recommendationTableRooms(recommendationState.minimumBasePriceRoomStates);
	if (rooms.length === 0) return null;

	const roomType = Utilities.snakeCaseToWords(recommendationState.roomMappingType);
	const findRoomAvailability = (roomName: string) => {
		const item = availabilityDailyState?.current?.items?.find(item => item.roomName.trim().toLowerCase() === roomName.trim().toLowerCase());
		return item?.availability ?? '-';
	};

	return <tbody className="divide-y divide-gray-200 bg-white">
	{rooms.map((room) => {
		const derivative = architecture.derivatives.find(d => d.pricingRoom.id === room.roomId);
		return <tr key={`room-${room.roomId}`} className={room.isAnchor ? 'bg-sky-50' : undefined}>
			<td className="px-1.5 py-2 font-medium text-gray-700 break-words">
				{room.roomName}
				{room.isAnchor && <span
					className="block font-light text-xs italic text-gray-400">(Base Price - {roomType})</span>}
				{!room.isAnchor && <span
					className="block font-light text-xs italic text-gray-400">(
					{derivative!.rule.type === PricingArchitectureDerivativeRuleType.SAME && 'Equal to base price - ' + roomType}
					{derivative!.rule.type === PricingArchitectureDerivativeRuleType.AMOUNT &&
						<span>{derivative!.rule.value! > 0 ? '+' : ''}{derivative!.rule.value}{Utilities.formatCurrency(companyHotel.settings.main.currency)} from base price - {roomType}</span>}
					{derivative!.rule.type === PricingArchitectureDerivativeRuleType.PERCENTAGE &&
						<span>{derivative!.rule.value! > 0 ? '+' : ''}{derivative!.rule.value}% from base price - {roomType}</span>}
					)</span>}
			</td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-700 text-right">
				{findRoomAvailability(room.roomName)}
			</td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-700">
				{Utilities.formatAmount(room.basePrice, companyHotel.settings.main.currency, 0)}
			</td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-500">
				{showRecommendation
					? Utilities.formatAmount(room.finalPrice, companyHotel.settings.main.currency, 0)
					: <span className="text-gray-400">—</span>}
			</td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-500">
				{showRecommendation &&
					<RenderDerivativeDifferenceRate basePrice={room.basePrice} calculatedPrice={room.finalPrice} />}
			</td>
		</tr>;
	})}
	</tbody>;
};

const RenderDerivativeDifferenceRate: React.FunctionComponent<{
	basePrice: number,
	calculatedPrice: number
}> = ({ basePrice, calculatedPrice }) => {
	const symbol = '%';
	const rate = roomPriceChangePercent(basePrice, calculatedPrice);
	if (rate === 0) {
		return null;
	}
	return rate > 0 ? <div className="inline-flex gap-x-0.5 items-center text-green-500">
		<ArrowUpIcon className="w-3 h-3" />
		<span>+{rate}{symbol}</span>
	</div> : <div className="inline-flex gap-x-0.5 items-center text-red-500">
		<ArrowDownIcon className="w-3 h-3" />
		<span>{rate}{symbol}</span>
	</div>;
};

const RenderDriverRow: React.FunctionComponent<{ driver: PriceRecommendationStateDriver }> = ({ driver }) => {
	const level = getDriverImpactLevel(driver.impact);
	const details = DRIVER_DETAILS[driver.type];
	const levelLabel = DRIVER_IMPACT_LEVEL_LABELS[level];

	return <tr>
		<td className="whitespace-nowrap pl-4 pr-3 py-2 font-medium text-gray-700">
			<div className="flex items-center gap-x-1.5">
				<span>{details.label}</span>
				{/* The design-system info button and card (it replaces a raw button and a react-tooltip). */}
				<InfoTooltip label={`About ${details.label}`} title={details.label}>
					{details.description}
					<span className="mt-3 flex items-center gap-x-2 border-t border-gray-200 pt-3">
						<span className="flex gap-x-1">
							{DRIVER_SEGMENT_COLORS.map(color => <span key={color} className={`h-2 w-6 rounded-full ${color}`} />)}
						</span>
						<span className="text-gray-500">None &rarr; Very high</span>
					</span>
				</InfoTooltip>
			</div>
		</td>
		<td className="px-3 py-2">
			<div className="flex items-center gap-x-3">
				<div className="flex h-3.5 items-center gap-x-1">
					{DRIVER_SEGMENT_COLORS.map((color, index) =>
						<span
							key={color}
							className={`h-2 w-6 rounded-full ${index < level ? color : 'bg-gray-200'}`} />)}
				</div>
				<span className={level === 0 ? 'text-xs italic text-gray-400' : 'text-xs text-gray-500'}>
					{levelLabel}
				</span>
			</div>
		</td>
	</tr>;
};


const RenderEditPricingButton: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	architectures: PricingArchitecture[],
	recommendationStates: PriceRecommendationState[],
	// The architecture of a room-type tab; undefined on the All tab.
	architectureIds?: number[],
	onSubmit: () => any,
	statDate: string
}> = ({ companyHotel, recommendationStates, architectureIds, onSubmit, architectures, statDate }) => {
	const { canChangePrices } = usePricingRights(companyHotel);
	const modal = useModal();
	const tools = useMarketStateTools();
	const queryClient = useQueryClient();
	const toaster = useToaster();
	const { t } = useTranslation();
	const directSubmitPending = React.useRef(false);
	const [directSubmitResult, setDirectSubmitResult] = useState<SubmitPricingResultOutput | null>(null);
	const submittedRecommendationStateIds =
		tools.findDailySnapshotState(statDate)?.marketStateDailySnapshot?.submittedRecommendationStateIds ?? [];
	const hasSubmittedRecommendation = recommendationStates.length > 0 && recommendationStates.every(recommendation =>
		submittedRecommendationStateIds.includes(recommendation.id)
	);
	// Direct Submit: the valid, not yet submitted recommendations of the open tab, as they are.
	const submittableStates = recommendationStates.filter(recommendation =>
		recommendation.isValid && !submittedRecommendationStateIds.includes(recommendation.id));
	const rateCount = companyHotel.hasRecommendationCanSubmitPrices() === true
		? submittableStates.reduce((count, recommendation) =>
			count + recommendationTableRooms(recommendation.minimumBasePriceRoomStates).length, 0)
		: 0;

	const directSubmitMutation = useMutation({
		mutationFn: PricingAutomationService.submitSelectivePricing,
		onMutate: () => setDirectSubmitResult(null),
		onSuccess: async (result) => {
			if (result.state === SubmitMonthlyPricingResultOutputState.SUCCESS) {
				await queryClient.invalidateQueries({ queryKey: MarketStateGraphQLQueryKeys.all });
				await queryClient.invalidateQueries({ queryKey: PricingAutomationQueryKeys.pricingSubmissions(companyHotel.id) });
				toaster.success('Pricing', 'Pricing submitted successfully!');
				tools.reloadMarketStateForDay(statDate);
				return;
			}
			setDirectSubmitResult(result);
		},
		onError: (error: string) => toaster.error('Pricing', t(error)),
		onSettled: () => { directSubmitPending.current = false; },
	});
	const directSubmitError = directSubmitResult?.errorCode ?? directSubmitResult?.errorMessage;

	// A READ user sees the drawer only (D4). Edit Pricing is on every day; the popup chooses its submit (R6, R7).
	if (!canChangePrices) return null;

	return <div className="flex w-full flex-col gap-y-3">
		{directSubmitResult !== null && directSubmitResult.blockedItems.length > 0 && <PriceSubmissionBlockedItems
			items={directSubmitResult.blockedItems}
			currency={companyHotel.settings.main.currency}
			onDateSelect={() => modal.open()} />}
		{directSubmitResult !== null && directSubmitResult.blockedItems.length === 0 && directSubmitError !== undefined && directSubmitError !== '' &&
			<Message state="error">{t(directSubmitError, ...(directSubmitResult.errorParams ?? []))}</Message>}
		<div className="flex items-center gap-x-2 justify-end">
			{hasSubmittedRecommendation && <Tag state="info">Pricing Submitted</Tag>}
			<FormButton state="white" disabled={directSubmitMutation.isPending} onClick={() => modal.open()}>Edit Pricing</FormButton>
			{rateCount > 0 && <FormButton state="primary"
											isLoading={directSubmitMutation.isPending}
											disabled={directSubmitMutation.isPending}
						onClick={() => {
							if (directSubmitPending.current || rateCount === 0) return;
							directSubmitPending.current = true;
							directSubmitMutation.mutate({
																			companyHotelId: companyHotel.id,
																			input: { recommendationIds: submittableStates.map(recommendation => recommendation.id) },
																		}); }}>
				Submit {rateCount} {rateCount === 1 ? 'rate' : 'rates'}
			</FormButton>}
		</div>
		<PriceEditModal ref={modal.ref}
			companyHotel={companyHotel}
			architectures={architectures}
			recommendationStates={recommendationStates}
			architectureIds={architectureIds}
			onSubmit={onSubmit}
			statDate={statDate} />
	</div>;
};

const formatRate = (rate: number) => `${rate > 0 ? '+' : ''}${Utilities.roundDouble(rate * 100, 1)}%`;

// The first card of the drawer content: what the pricing engine did for this date. A recommendation has its own card
// (RecommendedChangeCard); a room order, a day with no change or no result has the grey card. No card when a price rule
// set the rate: its box is the first block.
const RenderDayHeader: React.FunctionComponent<{
	recommendationStates: PriceRecommendationState[],
	dayStates?: PriceRecommendationState[],
	ruleApplies?: boolean
}> = ({ recommendationStates, dayStates = recommendationStates, ruleApplies = false }) => {
	const kind = findDayKind(recommendationStates);
	if (kind === 'valid' && ruleApplies) return null;
	const valid = recommendationStates.filter(state => state.isValid);
	const changes = valid.map(state => `${Utilities.snakeCaseToWords(state.roomMappingType)} ${formatRate(state.effectiveChangeRate)}`).join(' · ');
	const lifted = valid[0];
	const lower = lifted === undefined ? undefined : findLowerRoomType(lifted, dayStates, ROOM_MAPPING_TYPE_ORDER);
	const roomOrderText = lifted === undefined ? '' : `${Utilities.snakeCaseToWords(lifted.roomMappingType)} goes up to stay above ${lower === undefined
		? 'the cheaper room type' : Utilities.snakeCaseToWords(lower)}, as your room order sets. The market did not move this rate.`;
	const content: Record<DayKind, { title: string, text: string }> = {
		valid: { title: 'Recommended change', text: 'The pricing engine proposes new rates for this date. Review them below before you submit.' },
		roomOrder: { title: `Room order · ${changes}`, text: roomOrderText },
		held: { title: 'No rate change', text: 'The pricing engine keeps your current rates for this date.' },
		none: { title: 'No recommendation', text: 'The pricing engine has no result for this date.' },
	};
	const { title, text } = content[kind];
	if (kind === 'valid') return <RecommendedChangeCard states={valid} text={text} />;
	const frame = 'bg-gray-50 ring-black ring-opacity-10';
	return <div className={`overflow-hidden rounded-md ring-1 shadow-sm ${frame}`}>
		<div className="px-4 py-3">
			<h6 className="text-sm font-semibold text-gray-900">{title}</h6>
			<p className="mt-0.5 text-xs text-gray-500">{text}</p>
		</div>
	</div>;
};

// The card of a day with a recommendation: it stands out from the grey cards (an accent bar, the sparkles icon and a
// percent pill per room type, the same whole percent as the Change column of the table).
const RecommendedChangeCard: React.FunctionComponent<{ states: PriceRecommendationState[], text: string }> = ({ states, text }) =>
	<div className="overflow-hidden rounded-md bg-blue-50 ring-1 ring-blue-500 ring-opacity-30 shadow-sm border-l-4 border-blue-600">
		{/* pl-3 + the 4 px bar = 16 px: the text starts at the same edge as the titles of the other cards. */}
		<div className="pl-3 pr-4 py-3">
			<h6 className="flex items-center gap-x-1.5 text-sm font-semibold text-blue-900">
				<SparklesIcon className="h-4 w-4 text-blue-600" aria-hidden="true" />Recommended change
			</h6>
			<div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
				{states.map(state => <span key={state.id} className="inline-flex items-center gap-x-1.5 text-sm text-gray-700">
					{Utilities.snakeCaseToWords(state.roomMappingType)}
					<RateChange rate={recommendedChangeRate(state)} />
				</span>)}
			</div>
			<p className="mt-2 text-xs text-gray-500">{text}</p>
		</div>
	</div>;

// The table of a day with no engine result: the live WebHotelier rate of each room, no proposed rate.
const RenderLivePriceTable: React.FunctionComponent<{ roomsWithPrice: PricingRoomWithPriceOutput[] }> = ({ roomsWithPrice }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { availabilityDailyState } = React.useContext(AvailabilityDailyStateContext);
	const currency = companyHotel.settings.main.currency;
	const findRoomAvailability = (roomName: string) => availabilityDailyState?.current?.items
		?.find(item => item.roomName.trim().toLowerCase() === roomName.trim().toLowerCase())?.availability ?? '-';
	return <tbody className="divide-y divide-gray-200 bg-white">
	{roomsWithPrice.map(roomWithPrice => {
		const roomType = Utilities.snakeCaseToWords(roomWithPrice.roomMappingType);
		const rule = roomWithPrice.derivativeRule;
		return <tr key={`live-room-${roomWithPrice.room.id}`} className={roomWithPrice.isAnchor ? 'bg-sky-50' : undefined}>
			<td className="px-1.5 py-2 font-medium text-gray-700 break-words">
				{roomWithPrice.room.roomName}
				<span className="block font-light text-xs italic text-gray-400">(
					{roomWithPrice.isAnchor && 'Base Price - ' + roomType}
					{!roomWithPrice.isAnchor && rule?.type === PricingArchitectureDerivativeRuleType.SAME && 'Equal to base price - ' + roomType}
					{!roomWithPrice.isAnchor && rule?.type === PricingArchitectureDerivativeRuleType.AMOUNT &&
						`${Number(rule.value) > 0 ? '+' : ''}${rule.value}${Utilities.formatCurrency(currency)} from base price - ${roomType}`}
					{!roomWithPrice.isAnchor && rule?.type === PricingArchitectureDerivativeRuleType.PERCENTAGE &&
						`${Number(rule.value) > 0 ? '+' : ''}${rule.value}% from base price - ${roomType}`}
					)</span>
			</td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-700 text-right">{findRoomAvailability(roomWithPrice.room.roomName)}</td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-700">
				{roomWithPrice.price === null ? '—' : Utilities.formatAmount(roomWithPrice.price, currency, 0)}
			</td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-500"><span className="text-gray-400">—</span></td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-500" />
		</tr>;
	})}
	</tbody>;
};

const NoEngineResultContents: React.FunctionComponent<{
	statDate: string,
	architectures: PricingArchitecture[],
	onSubmitPrices: () => any,
}> = ({ statDate, architectures, onSubmitPrices }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { data, isLoading, isError } = useQuery({
		queryKey: PricingAutomationQueryKeys.pricingRoomsWithPrice(companyHotel.id, statDate),
		queryFn: () => PricingAutomationService.findCompanyHotelPricingRoomWithPrice(companyHotel.id, statDate),
	});
	return <div className="mb-6 mt-6 space-y-4">
		<RenderDayHeader recommendationStates={[]} />
		{/* Booking Pickup with showTotalsOnly reads only the date (the hotel totals), so a day with no state shows it too. */}
		<PriceRecommendationDrawerBookingPace showTotalsOnly
			recommendation={{ statDate, roomMappingType: HotelRoomMappingType.STANDARD } as Pick<PriceRecommendationState, 'statDate' | 'roomMappingType'> as PriceRecommendationState} />
		{isLoading && <TablePlaceholder />}
		{isError && <Message state="error">The WebHotelier rates could not load.</Message>}
		{!_isEmpty(data) && <PriceTableWrapper hasProposal={false}><RenderLivePriceTable roomsWithPrice={data!} /></PriceTableWrapper>}
		<div className="flex gap-x-2 justify-end -mt-3">
			<RenderEditPricingButton companyHotel={companyHotel}
				recommendationStates={[]}
				architectures={architectures}
				onSubmit={onSubmitPrices}
				statDate={statDate} />
		</div>
	</div>;
};

export default PriceRecommendationDrawerContents;
